import { useState, type ReactNode } from 'react';
import { Button, Dialog, Field, Input, Textarea } from '@paige/ui';
import { useFormat } from '@paige/format';
import type {
  ProblemAction,
  ProblemActRequest,
  ProblemActResult,
  ProblemFile,
  ProblemGroup,
} from './problem-types';
import { actionWords, countWords, fileWords, groupHeadline } from './problem-words';

/** What was asked, while its note is being written: a whole row, or some of its files. */
export interface PendingAction {
  group: ProblemGroup;
  action: ProblemAction;
  files?: ProblemFile[];
}

export interface ProblemActionDialogProps {
  /** Null closes the dialog. */
  pending: PendingAction | null;
  onClose: () => void;
  onConfirm: (request: ProblemActRequest) => Promise<ProblemActResult>;
}

/** A week from today, as the date box's starting value (yyyy-MM-dd, the viewer's day). */
function inAWeek(): string {
  const d = new Date(Date.now() + 7 * 86_400_000);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

/** The end of the picked day in the viewer's zone, as an ISO time; undefined for "until taken back". */
function untilOf(day: string): string | undefined {
  if (!day) return undefined;
  const end = new Date(`${day}T23:59:59`);
  return Number.isNaN(end.getTime()) ? undefined : end.toISOString();
}

/** The note, the date and the send: on success both clear and the dialog closes; on failure it stays and says why. */
function useConfirm(props: ProblemActionDialogProps) {
  const [note, setNote] = useState('');
  const [day, setDay] = useState(inAWeek);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const close = () => {
    setFailed(null);
    props.onClose();
  };
  const confirm = async (p: PendingAction) => {
    setBusy(true);
    setFailed(null);
    try {
      const until = p.action === 'acknowledge' ? untilOf(day) : undefined;
      await props.onConfirm({ ...p, note: note.trim(), ...(until ? { until } : {}) });
      setNote('');
      props.onClose();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return { note, setNote, day, setDay, busy, failed, confirm, close };
}

type Confirm = ReturnType<typeof useConfirm>;

/** The date an acknowledgement lasts until (acknowledging only), the note, and why the last try failed. */
function Body({ action, c }: { action: ProblemAction; c: Confirm }): ReactNode {
  return (
    <>
      {action === 'acknowledge' ? (
        <Field
          label="Until"
          hint="Through the end of that day. Leave empty to keep it until someone takes it back."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              value={c.day}
              onChange={(e) => c.setDay(e.currentTarget.value)}
            />
          )}
        </Field>
      ) : null}
      <Field label="Why" hint="Kept on the company's audit trail with your name." required>
        {({ id, describedBy }) => (
          <Textarea
            id={id}
            aria-describedby={describedBy}
            value={c.note}
            onChange={(e) => c.setNote(e.target.value)}
            rows={3}
            maxLength={500}
          />
        )}
      </Field>
      {c.failed ? (
        <p className="pb-dialog__failed" role="alert">
          Nothing was changed. {c.failed}
        </p>
      ) : null}
    </>
  );
}

function Buttons({ c, label, onGo }: { c: Confirm; label: string; onGo: () => void }): ReactNode {
  return (
    <>
      <Button variant="secondary" onClick={c.close} disabled={c.busy}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onGo} disabled={c.busy || c.note.trim() === ''}>
        {c.busy ? 'Working' : label}
      </Button>
    </>
  );
}

/**
 * The confirm step, in the page: what will happen, to what, and why (the note that stays on the company's audit
 * trail with the staff account). Acknowledging also takes the date it lasts until.
 */
export function ProblemActionDialog(props: ProblemActionDialogProps): ReactNode {
  const f = useFormat();
  const c = useConfirm(props);
  const pending = props.pending;
  if (!pending) return null;
  const { group, action, files } = pending;
  const words = actionWords(group, action);
  const label = files ? words.one : words.all;
  const what = files?.length === 1 ? fileWords(f, files[0]!) : countWords(f, group);
  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : c.close())}
      title={`${label}: ${what}`}
      description={`${group.company.name}. ${groupHeadline(group)}. ${words.effect}`}
      actions={<Buttons c={c} label={label} onGo={() => void c.confirm(pending)} />}
    >
      <Body action={action} c={c} />
    </Dialog>
  );
}
